import { createContext, useContext, useEffect } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { COLLEGE_NESTED_FIELDS } from "data"
import {
  FieldValues,
  FormProvider as ReactHookFormProvider,
  useForm,
  UseFormReturn,
} from "react-hook-form"
import { sendTealiumEvent } from "tealium"

import { ErrorModalAction } from "components/AppErrorModal"

import { UserApi } from "api/user"

import { ClientContext } from "context/ClientContext"
import { useErrorModal } from "context/ErrorContext"

import {
  alphabetizeObject,
  getFinalData,
  isAuthorizationError,
  isFieldEmpty,
  isForbiddenError,
  sanitizeFormValues,
  transformServerDataToFormData,
} from "utilities/helpers"
import { log, SHOW_LOGS } from "utilities/logging"
import { ValidationSchema } from "utilities/schema"

import { User } from "types/user"

interface FormContextProps {
  validationSchema: ValidationSchema
  user: User
  token: string
  children?: React.ReactNode
}

export interface FormContextState {
  form: UseFormReturn<FieldValues, object>
  onSubmit: () => Promise<User | void>
  onSave: () => Promise<User | void>
}

// We'll create the context without an initial value since it will always be provided by FormContextProvider
const FormContext = createContext<FormContextState>(null!)
export default FormContext

export const FormContextProvider: React.FC<FormContextProps> = ({
  validationSchema,
  user,
  token,
  children,
}) => {
  const { client } = useContext(ClientContext)
  const { handleError } = useErrorModal()

  if (!client) {
    throw new Error("Client is required")
  }

  const arrayData = transformServerDataToFormData(
    { ...user.applicationData },
    COLLEGE_NESTED_FIELDS[client.theme as keyof typeof COLLEGE_NESTED_FIELDS],
  )

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues: arrayData,
  })

  // On mount, validate fields that already have values
  useEffect(() => {
    if (validationSchema) {
      const values = form.getValues()
      Object.keys(values).forEach(fieldName => {
        const value = values[fieldName]
        // Don't try to validate empty fields
        if (!isFieldEmpty(value)) {
          form.trigger(fieldName, { shouldFocus: false })
        }
      })
    }
  }, [form, validationSchema])

  const onSubmit = (): Promise<User | void> => {
    // Get data without hidden and empty fields in server format
    const data = getFinalData(
      form.getValues,
      { data: client.data, theme: client.theme },
      form.setValue,
    )

    const sanitizedData = sanitizeFormValues(data || {})

    return UserApi.finalize({
      token: token,
      user: {
        ...user,
        applicationData: {
          ...sanitizedData,
          queryParams:
            sanitizedData?.queryParams ||
            (localStorage.getItem("queryParams")
              ? JSON.parse(localStorage.getItem("queryParams")!)
              : undefined),
        },
      },
    })
      .then(user => {
        if (user.submittedAt) {
          sendTealiumEvent("app_submit", {
            type: "link",
            email_address: user.email,
          })
        }
        return user
      })
      .catch(err => {
        if (isAuthorizationError(err)) {
          handleError({
            action: ErrorModalAction.LOGOUT,
            log: { message: "Authorization error onSubmit", err },
          })
        } else if (isForbiddenError(err)) {
          // 403 = Session expired, redirect to login with message
          handleError({
            action: ErrorModalAction.LOGOUT,
            log: { message: "Session expired onSubmit", err },
            message:
              "Your session has expired. Please sign in again to continue.",
          })
        } else {
          console.error(err)
        }
      })
      .finally(() => {
        if (localStorage.getItem("queryParams")) {
          localStorage.removeItem("queryParams")
        }
      })
  }

  const onSave = (): Promise<User | void> => {
    // Get data without hidden and empty fields in server format
    const data = getFinalData(
      form.getValues,
      { data: client.data, theme: client.theme },
      form.setValue,
    )

    const sanitizedData = sanitizeFormValues(data || {})

    return UserApi.replace({
      token: token,
      user: {
        ...user,
        applicationData: {
          ...sanitizedData,
          queryParams:
            sanitizedData?.queryParams ||
            (localStorage.getItem("queryParams")
              ? JSON.parse(localStorage.getItem("queryParams")!)
              : undefined),
        },
      },
    })
      .then(user => {
        const userFormData = alphabetizeObject(user.applicationData)
        log(
          SHOW_LOGS.UPDATED_USER_FORM_VALUES,
          "%cUpdated form values:",
          "font-weight: bold",
          JSON.stringify(userFormData, null, 2),
        )
        return user
      })
      .catch(err => {
        if (isAuthorizationError(err)) {
          handleError({
            action: ErrorModalAction.LOGOUT,
            log: `Authorization error onSave: ${err}`,
          })
        } else if (isForbiddenError(err)) {
          // 403 = Session expired during auto-save
          // Don't immediately show error modal, just log out silently and they'll be redirected
          console.warn("Session expired during auto-save, redirecting to login")
          handleError({
            action: ErrorModalAction.LOGOUT,
            log: `Session expired onSave: ${err}`,
            message:
              "Your session has expired. Please sign in again to continue.",
          })
        } else {
          console.error(err)
        }
      })
  }

  const formContextState: FormContextState = {
    form,
    onSubmit,
    onSave,
  }

  return (
    <FormContext.Provider value={formContextState}>
      <ReactHookFormProvider {...form}>{children}</ReactHookFormProvider>
    </FormContext.Provider>
  )
}
